/**
 * AdminMacroRecorderRouter — routes an authorized admin peer's macroRecorder* data-channel messages
 * (screenshots, record control, legacy input capture, raw-package capture, capture sources) and sends replies.
 * AdminPeerHandler has already checked that the sender is the active admin session.
 */
import * as Sentry from '@sentry/react';
import type { PeerCoordinator } from '../PeerCoordinator';
import type { RecordingControlManager } from '../../../services/playjector/managers/RecordingControlManager';
import type { LocalHostSessionCoordinator } from '../../local-host-session/LocalHostSessionCoordinator';
import type { MacroRecorderCaptureSourceManager } from '../../../services/playjector/managers/MacroRecorderCaptureSourceManager';

/** Actions that carry a takeId we can validate before relaying to Playjector. */
const TAKEID_BEARING_ACTIONS = new Set(['macroRecorderScreenshot', 'macroRecorderRecordControl']);
const MACRO_TAKEID_UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function isMacroUuid(value: unknown): value is string {
  return typeof value === 'string' && MACRO_TAKEID_UUID_RE.test(value);
}

const MACRO_RECORDER_ACTIONS = new Set([
  'macroRecorderScreenshot',
  'macroRecorderRecordControl',
  'macroRecorderInputCapture',
  'macroRecorderUploadScreenshots',
  'macroRecorderKeepalive',
  'macroRecorderCaptureSources',
  'macroRecorderCaptureSourceSelect',
  'macroPackageCapture',
  'macroPackageValidate',
  'macroPackageCapabilities',
  'macroPackageUpload',
  'macroPackageRecordingCatalog',
]);

export function isMacroRecorderMessage(msg: Record<string, unknown> | undefined, headerAction?: string): boolean {
  if (headerAction && MACRO_RECORDER_ACTIONS.has(headerAction)) return true;
  const action = msg?.action as string | undefined;
  return !!action && MACRO_RECORDER_ACTIONS.has(action);
}

export interface AdminMacroRecorderRouterDeps {
  peerCoordinator: Pick<PeerCoordinator, 'sendToPeer'>;
  recordingControlManager?: RecordingControlManager;
  captureSourceManager?: MacroRecorderCaptureSourceManager;
  localHostSession?: Pick<LocalHostSessionCoordinator, 'handleRecorderInput' | 'handlePackageCapture' | 'handlePackageValidation' | 'handlePackageCapabilities' | 'handlePackageUpload' | 'handleRecordingCatalog'>;
}

export class AdminMacroRecorderRouter {
  public constructor(private readonly deps: AdminMacroRecorderRouterDeps) {}

  public handle(peerId: string, msg: Record<string, unknown>, headerAction?: string): void {
    const action = headerAction || (msg.action as string);

    if (action === 'macroRecorderKeepalive') {
      Sentry.logger.info('[AdminPeerHandler] macro-recorder macroRecorderKeepalive received', {
        feature: 'macro-recorder', transport: 'webrtc-dc', direction: 'receive', action,
      });
      return;
    }

    const sourcePayload = (msg.message as Record<string, unknown> | undefined) ?? msg;
    if (action === 'macroPackageRecordingCatalog') {
      void (this.deps.localHostSession?.handleRecordingCatalog(peerId, sourcePayload) ?? Promise.resolve(null))
        .then(reply => this.sendDataChannelReply(peerId, action, reply));
      return;
    }
    if (action === 'macroPackageUpload') {
      this.sendDataChannelReply(peerId, action, this.deps.localHostSession?.handlePackageUpload(peerId, sourcePayload) ?? null);
      return;
    }
    if (action === 'macroPackageCapabilities') {
      const response = this.deps.localHostSession?.handlePackageCapabilities(sourcePayload)
        ?? Promise.resolve({ requestId: String(sourcePayload.requestId ?? ''), success: false, error: 'host_capabilities_unavailable' });
      void response.then(reply => this.sendDataChannelReply(peerId, action, reply));
      return;
    }
    if (action === 'macroRecorderInputCapture') {
      this.deps.localHostSession?.handleRecorderInput(peerId, sourcePayload);
      return;
    }
    if (action === 'macroPackageCapture') {
      if (this.deps.localHostSession) this.deps.localHostSession.handlePackageCapture(peerId, sourcePayload);
      else this.sendDataChannelReply(peerId, action, { requestId: String(sourcePayload.requestId ?? ''),
        packageId: String(sourcePayload.packageId ?? ''), success: false, events: [], more: false, stopped: true,
        error: 'input_capture_unavailable' });
      return;
    }
    if (action === 'macroPackageValidate') {
      if (this.deps.localHostSession) void this.deps.localHostSession.handlePackageValidation(peerId, sourcePayload)
        .then(reply => this.sendDataChannelReply(peerId, action, reply));
      else this.sendDataChannelReply(peerId, action, { requestId: String(sourcePayload.requestId ?? ''), success: false, error: 'macro_sessions_unavailable' });
      return;
    }
    if (action === 'macroRecorderCaptureSources' || action === 'macroRecorderCaptureSourceSelect') {
      this.handleCaptureSourceMessage(peerId, action, sourcePayload);
      return;
    }

    const manager = this.deps.recordingControlManager;
    if (!manager) {
      console.warn('[AdminPeerHandler] macroRecorder message received but no RecordingControlManager configured');
      Sentry.captureMessage('[MACRO-SCREENSHOT] macroRecorder message dropped — no RecordingControlManager', {
        level: 'error',
        extra: { action },
      });
      return;
    }

    const message = msg as Record<string, unknown> | undefined;
    if (!message) {
      console.warn('[AdminPeerHandler] macroRecorder message missing message body');
      Sentry.captureMessage('[MACRO-SCREENSHOT] macroRecorder message missing message body', {
        level: 'warning',
        extra: { action },
      });
      return;
    }

    // Defensive unwrap: InputManager forwards the already-inner payload
    // (body.message), but tolerate a doubly-wrapped { message: {...} } so a
    // mis-nested envelope never strands the takeId (which would look "empty" to
    // the guards below and to Playjector).
    const payload = (message.message as Record<string, unknown> | undefined) ?? message;
    const requestId = payload.requestId as string | undefined;
    const takeId = payload.takeId as string | undefined;
    const receiveTs = Date.now();

    Sentry.logger.info('[AdminPeerHandler] macro-recorder DC receive macroRecorder action', {
      feature: 'macro-recorder', transport: 'webrtc-dc', direction: 'receive',
      action, requestId, takeId,
    });

    Sentry.addBreadcrumb({
      category: 'MACRO-SCREENSHOT',
      level: 'info',
      message: 'inbound macroRecorder message',
      data: { action, requestId, takeId },
    });

    // High-signal instrumentation: the dashboard is fully guarded, so a missing /
    // non-UUID takeId arriving here means the value is entering from an
    // un-instrumented sender (stale build, alternate emitter) or is being lost in
    // transit. Capture the raw inbound payload once at the boundary so the next
    // run reveals the true origin. (Header source is not plumbed to this handler.)
    if (TAKEID_BEARING_ACTIONS.has(action) && !isMacroUuid(payload.takeId)) {
      Sentry.captureMessage('[MACRO-DEBUG] macroRecorder message with bad takeId', {
        level: 'warning',
        extra: {
          action,
          peerId,
          requestId: payload.requestId,
          takeId: payload.takeId,
          rawMessage: JSON.stringify(message).slice(0, 2000),
        },
      });
    }

    if (action === 'macroRecorderScreenshot') {
      void manager
        .handleMacroRecorderScreenshot(payload as any)
        .then((reply) => {
          if (!reply.success) {
            Sentry.captureMessage('[MACRO-SCREENSHOT] screenshot reply success=false', {
              level: 'warning',
              extra: { requestId, error: reply.error, takeId },
            });
          }
          this.sendDataChannelReply(peerId, 'macroRecorderScreenshot', reply);
          Sentry.logger.info('[AdminPeerHandler] macro-recorder DC reply sent macroRecorderScreenshot', {
            feature: 'macro-recorder', transport: 'webrtc-dc', direction: 'send',
            action, requestId, takeId, latencyMs: Date.now() - receiveTs,
          });
        })
        .catch((err) => {
          Sentry.captureMessage('[MACRO-SCREENSHOT] handleMacroRecorderScreenshot threw', {
            level: 'error',
            extra: {
              requestId,
              takeId,
              error: err instanceof Error ? err.message : String(err),
            },
          });
          this.sendDataChannelReply(peerId, 'macroRecorderScreenshot', {
            requestId,
            success: false,
            error: err instanceof Error ? err.message : String(err),
          });
        });
    } else if (action === 'macroRecorderRecordControl') {
      void manager.handleRecordControl(payload as any).then((reply) => {
        this.sendDataChannelReply(peerId, 'macroRecorderRecordControl', reply);
        Sentry.logger.info('[AdminPeerHandler] macro-recorder DC reply sent macroRecorderRecordControl', {
          feature: 'macro-recorder', transport: 'webrtc-dc', direction: 'send',
          action, requestId, takeId, latencyMs: Date.now() - receiveTs,
        });
      }).catch((err) => {
        Sentry.captureMessage('[MACRO-SCREENSHOT] handleRecordControl threw', {
          level: 'error',
          extra: { requestId, takeId, error: err instanceof Error ? err.message : String(err) },
        });
        this.sendDataChannelReply(peerId, 'macroRecorderRecordControl', {
          requestId,
          status: 'error',
          error: err instanceof Error ? err.message : String(err),
        });
      });
    } else if (action === 'macroRecorderUploadScreenshots') {
      void manager.handleUploadScreenshots(payload as any).then((reply) => {
        this.sendDataChannelReply(peerId, 'macroRecorderUploadScreenshots', reply);
        Sentry.logger.info('[AdminPeerHandler] macro-recorder DC reply sent macroRecorderUploadScreenshots', {
          feature: 'macro-recorder', transport: 'webrtc-dc', direction: 'send',
          action, requestId, takeId, latencyMs: Date.now() - receiveTs,
        });
      }).catch((err) => {
        Sentry.captureMessage('[MACRO-SCREENSHOT] handleUploadScreenshots threw', {
          level: 'error',
          extra: { requestId, takeId, error: err instanceof Error ? err.message : String(err) },
        });
        this.sendDataChannelReply(peerId, 'macroRecorderUploadScreenshots', {
          requestId,
          results: [],
          error: err instanceof Error ? err.message : String(err),
        });
      });
    } else {
      Sentry.logger.info('[AdminPeerHandler] macro-recorder Unknown macroRecorder action', {
        feature: 'macro-recorder', action,
      });
    }
  }

  private handleCaptureSourceMessage(
    peerId: string,
    action: 'macroRecorderCaptureSources' | 'macroRecorderCaptureSourceSelect',
    payload: Record<string, unknown>,
  ): void {
    const manager = this.deps.captureSourceManager;
    const requestId = String(payload.requestId ?? '');
    if (!manager) {
      this.sendDataChannelReply(peerId, action, { requestId, success: false, error: 'capture_source_manager_unavailable' });
      return;
    }
    if (action === 'macroRecorderCaptureSources') {
      void manager.listSources(payload.captureElementName as string | undefined)
        .then((result) => this.sendDataChannelReply(peerId, action, { requestId, success: true, ...result }))
        .catch((error) => this.sendDataChannelReply(peerId, action, {
          requestId, success: false, sources: [], selectedId: '',
          error: error instanceof Error ? error.message : 'capture_source_list_failed',
        }));
      return;
    }
    void manager.selectSource(payload.source as never)
      .then((selectedId) => this.sendDataChannelReply(peerId, action, { requestId, success: true, selectedId }))
      .catch((error) => this.sendDataChannelReply(peerId, action, {
        requestId, success: false,
        error: error instanceof Error ? error.message : 'capture_source_select_failed',
      }));
  }

  private sendDataChannelReply(peerId: string, action: string, message: unknown): void {
    const payload = JSON.stringify({
      header: { target: 'system', action, isReply: true, source: 'host' },
      body: { message },
    });
    this.deps.peerCoordinator.sendToPeer(peerId, payload);
  }
}
